import {useState} from 'react';
import {HttpError, Problem} from '@synako/halitrek';
import {useTranslation} from 'react-i18next';
import {
  ItemCatalogue2StateFF,
  getAddArticlePayloadWithColisFallback,
  toDecimalFr,
  useLivraisonsPossiblesContext,
} from 'ff-common';
import {useAjouterAuPanier} from '@/hooks/livraison/use-ajouter-au-panier';
import {useSimpleTextToast} from '@/hooks/useSimpleTextToast';
import {useDefaultOnError} from '@/hooks/useDefaultOnError';

export type AjouterAvecPrixSaisi = (args: {
  prix: number;
  modeTarifaire: 'saisir_one_shot' | 'saisir_dero';
}) => Promise<boolean>;

/**
 * Saisie de prix avant ajout, appelée par l'écran qui ajoute au panier
 * (à côté de `useBuyingBlocks`). `PrixBlockUnitaire` ne fait que recevoir le résultat.
 * `itemState` absent (carte marketplace) : saisie désactivée, le hook reste appelé.
 */
export const useAjouterAvecPrixSaisi = (
  itemState: ItemCatalogue2StateFF | undefined,
  commandeEnColis: boolean,
): {
  ajouterAvecPrixSaisi: AjouterAvecPrixSaisi;
  isAddingAvecPrix: boolean;
  saisieAvantAjout: boolean;
  codeArticle: string | undefined;
} => {
  const {t} = useTranslation();
  const textToast = useSimpleTextToast();
  const onError = useDefaultOnError(textToast);
  const {livraisonSelectionnee} = useLivraisonsPossiblesContext();
  const ajouterAuPanier = useAjouterAuPanier(
    livraisonSelectionnee.data.id,
    textToast,
  );
  const [isSubmitting, setIsSubmitting] = useState(false);

  const code = itemState?.data.code ?? '';

  const ajouterAvecPrixSaisi: AjouterAvecPrixSaisi = async ({
    prix,
    modeTarifaire,
  }) => {
    setIsSubmitting(true);
    try {
      const event = await ajouterAuPanier.submitAsync({
        code_article: code,
        ...getAddArticlePayloadWithColisFallback({
          commandeEnColis,
          quantiteColisEntier: itemState?.data.quantite_colis_entier,
          minimumCommande: itemState?.data.minimum_de_commande_client ?? 1,
          multipleCommande: itemState?.data.multiple_de_commande_client ?? 1,
        }),
        prix_dero_souhaite: prix,
        mode_tarifaire_souhaite: modeTarifaire,
      });

      const alerte = event.data.data.alerte_prix_plancher_depasse;
      // `!= null` intentionnel : couvre null et undefined (events legacy).
      if (alerte != null) {
        const ligne = event.data.data.ligne_de_panier;
        textToast(
          t('back_office.devis_personnalises.alerte_prix_plancher_depasse', {
            libelle: ligne.libelle,
            code_article: ligne.code_article,
            prix_saisi: toDecimalFr(alerte.prix_saisi),
            prix_plancher: toDecimalFr(alerte.prix_plancher),
          }),
          {duration: 999999999, variant: 'warning'},
        );
      }

      await ajouterAuPanier.notifierAjoutEtRupture(event);
      return true;
    } catch (error) {
      onError(error as HttpError | Problem);
      return false;
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    ajouterAvecPrixSaisi,
    isAddingAvecPrix: isSubmitting,
    saisieAvantAjout: !!itemState && !itemState.data.tolede.precommande,
    codeArticle: itemState?.data.code,
  };
};
